import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';
import { Chat } from '@material-ui/icons';

import useIsCompany from '../../../myHooks/useIsCompany';
import { algorithmCalculation, brandBuyerComparisonAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { fetchOwnedAgencyDataBySurveyId } from '../../../redux/actions/Data';
import { makeURL, findLatestExistingBatchData, batchDataSort } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';
import ChartCalculations from './chartCalculations';

import HeaderSelector from '../../../myComponents/headerSelector';
import Filters from '../../../myComponents/filters/Filters';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import ChatBot from '../../../myComponents/chatBot';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';

const useStyles = makeStyles(theme => ({
  chatButton: {
    display: 'flex',
    margin: '10px 0',
    padding: '7px 25px',
    borderRadius: '5px',
    alignItems: 'center',
    backgroundColor: '#19989B',

    '&.MuiButton-root:hover.Mui-disabled': {
      backgroundColor: '#19989B',
      color: '#fff !important',
    },

    '&.MuiButton-root.Mui-disabled': {
      backgroundColor: '#19989B',
      color: '#fff !important',
    },

    '&:hover': {
      backgroundColor: '#19878a',
    },

    '@media (max-width: 768px)': {
      padding: '5px 20px',
      marginLeft: 'auto',
    },

    '@media (min-width: 1300px)': {
      margin: '15px 0',
      padding: '5px 25px',
    },
  },
  chatButtonText: {
    color: 'white',
    fontSize: '12px',
    margin: 0,

    '@media (max-width: 768px)': {
      fontSize: '10px',
    },

    '@media (min-width: 1300px)': {
      fontSize: '15px',
    },
  },
  chatIcon: {
    fontSize: '20px',
    color: 'white',
    marginRight: '7px',

    '@media (max-width: 768px)': {
      fontSize: '13px',
    },
  },
  headerSelectorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gridColumnGap: '40px',

    '@media (max-width: 768px)': {
      gridColumnGap: '30px',
      width: '100%',
      alignItems: 'flex-start',
    },
  },
}));

const BrandDashboard = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isCompany = useIsCompany();
  const appData = useSelector(({ data }) => data);
  const [brandPersonaPickedId, setBrandPersonaPickedId] = useState(null);

  const classes = useStyles();

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  const [pickedBrandPersona, setPickedBrandPersona] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);
  const [chosenFilters, setChosenFilters] = useState(null);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  const [calculationType, setCalculationType] = useState('batch');
  const [pickedBuyerPersona, setPickedBuyerPersona] = useState(null);
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [chatOpened, setChatOpened] = useState(false);

  useEffect(() => {
    if (!appData.loading && !appData.loadingAgencyData && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn('Application could not load');
      } else {
        if (!id) {
          history.push(makeURL('/brand-personas/all'));
        } else {
          const pickedBrandPersona = !appData.brandPersonas ? false : appData.brandPersonas.find(e => e.id === id);
          if (!pickedBrandPersona) {
            if (!isCompany) return history.push(makeURL('/brand-personas/all'));
            else {
              dispatch(fetchOwnedAgencyDataBySurveyId(id, 'brand'));
              setUseEffectShouldTrigger(true);
            }
          } else {
            const defaultBuyerPersona =
              appData.buyerPersonas && appData.buyerPersonas.length ? appData.buyerPersonas[0] : null;
            setPickedBuyerPersona(defaultBuyerPersona);

            if (!pickedBatchData) {
              const { latestQuarter, latestYear } = findLatestExistingBatchData(
                appData.combinedBatchDataCodes[DEFAULT_BATCH_COUNTRY],
              );
              const pickedBatchToSet = {
                quarter: [latestQuarter],
                year: latestYear,
              };

              setPickedBatchData(pickedBatchToSet);
              setPickedBatchCountry(DEFAULT_BATCH_COUNTRY);

              brandPersonaChangeHandler(pickedBrandPersona, pickedBatchToSet, DEFAULT_BATCH_COUNTRY);
            } else {
              brandPersonaChangeHandler(pickedBrandPersona, pickedBatchData, pickedBatchCountry);
            }

            setPageIsLoading(false);
          }
        }
      }
    }
  }, [appData.loading, appData.loadingAgencyData, id]);

  useEffect(() => {
    if (brandPersonaPickedId) {
      history.push(makeURL(`/brand-personas/${brandPersonaPickedId}`));
      setUseEffectShouldTrigger(true);
    }
  }, [brandPersonaPickedId]);

  const brandPersonaCalculateHandler = (pickedPersona, filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    algorithmCalculation(
      'Brand',
      {
        persona: pickedPersona,
        filters: filters,
        batches: newPickedBatchData,
        batchCountry: newPickedBatchCountry,
      },
      dispatch,
      appData.visibleAgencies,
    )
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn('Calculation failed');
      });
  };

  const brandPersonaChangeHandler = (pickedPersona, newPickedBatchData, newPickedBatchCountry) => {
    setPickedBrandPersona(pickedPersona);
    brandPersonaCalculateHandler(pickedPersona, chosenFilters, newPickedBatchData, newPickedBatchCountry);
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    brandPersonaCalculateHandler(pickedBrandPersona, preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const buyerPersonaChangeHandler = id => {
    const foundBuyerPersona = appData.buyerPersonas.find(e => e.id === id);
    setPickedBuyerPersona(foundBuyerPersona);

    buyerPersonaCalculateHandler(foundBuyerPersona);
  };

  const buyerPersonaCalculateHandler = pickedPersona => {
    if (!pickedBrandPersona || !pickedPersona) {
      showToastWarn('Choose an audience persona before comparing');
      return;
    }

    setCalculations(prevState => ({ ...prevState, loading: true }));

    brandBuyerComparisonAlgorithmCalculation(pickedBrandPersona, pickedPersona)
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn('Calculation failed');
      });
  };

  const onChangeCalculationType = event => {
    const {
      target: { value },
    } = event;
    setCalculationType(value);

    if (value === 'batch') {
      brandPersonaCalculateHandler(pickedBrandPersona, chosenFilters, pickedBatchData, pickedBatchCountry);
    } else if (pickedBuyerPersona) {
      buyerPersonaCalculateHandler(pickedBuyerPersona);
    } else {
      showToastWarn('Choose an audience persona before comparing');
    }
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.combinedBatchDataCodes[value])
      .sort()
      .pop();
    const quarters = appData.combinedBatchDataCodes[value][newestBatchYear].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    brandPersonaCalculateHandler(pickedBrandPersona, chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    brandPersonaCalculateHandler(pickedBrandPersona, chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.combinedBatchDataCodes[pickedBatchCountry][value].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    brandPersonaCalculateHandler(pickedBrandPersona, chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = message => {
    toast.warn(message, {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  //I have an issue that when i change the data to 2022, then change the brand persona, calculation happens in 2022 but on screen its 2023
  const isBatchCalculation = calculationType === 'batch';

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
      {calculations.results ? (
        <div>
          <UserDropdownHeader />
          {pickedBrandPersona && (
            <ChatBot pickedPersona={pickedBrandPersona} chatOpened={chatOpened} setChatOpened={setChatOpened} type="brand" />
          )}

          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            flexWrap="wrap"
            alignItems="center"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff', gap: '30px' }}>
            <Box flex="1">
              <Box className={classes.headerSelectorContainer}>
                <HeaderSelector
                  selectedId={brandPersonaPickedId ? brandPersonaPickedId : id}
                  setPickedId={setBrandPersonaPickedId}
                  data={appData}
                  type="brand"
                  redirect={true}
                  disabled={!isBatchCalculation}
                />
                <Button onClick={() => setChatOpened(!chatOpened)} className={classes.chatButton}>
                  <Chat className={classes.chatIcon} />
                  <Typography component="div" variant="button" className={classes.chatButtonText}>
                    CHAT
                  </Typography>
                </Button>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <BuyerBatchSelector
                calculationType={calculationType}
                onChangeCalculationType={onChangeCalculationType}
                pickedBuyerPersona={pickedBuyerPersona}
                onChangeBuyerPersona={buyerPersonaChangeHandler}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                showBuyerPicker
                type="brand"
              />
            </Box>
          </Box>
          <Filters
            disabled={!isBatchCalculation}
            calculateHandler={onFiltersSubmit}
            themeColor="green"
            pickedBatchCountry={pickedBatchCountry}
            pickedBatchData={pickedBatchData}
          />

          {calculations.results.no_data ? (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
              <Typography
                variant="h6"
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  margin: '0',
                  padding: '0',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                No data, choose other "Consumer Settings" combination.
              </Typography>
            </Box>
          ) : (
            <ChartCalculations
              type="brand"
              data={calculations.results}
              brandPersona={pickedBrandPersona}
              isSelectedSample={isBatchCalculation}
            />
          )}
        </div>
      ) : null}
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default BrandDashboard;
